import "./CardNumber.css";

import {
  VALIDATION_STATUSES,
  ELEMENTS,
  EVENTS,
  ELEMENT_FRAMES,
  FRAMES_MODES,
} from "../../constants/";
import {
  sendViaPostMessage,
  sendToElementIframe,
  sendToControllerIframe,
} from "../../utils";
import { removeSpaces } from "../../utils/string";
import { handleCustomInputStyle } from "../../utils/stylesheet";
import {
  getNewValidationStatus,
  onMessage,
  registerFocusHandler,
  setOnMouseOverClasses,
  setOnMouseOutClasses,
  setOnBlurClasses,
  setOnFocusClasses,
  fixIE11InputCursor,
  wrapOnInputForIE11,
  wrapOnInputForCompositionText,
  onHiddenInputChange,
  onAutofillInput,
  simulateAutofill,
  onBackwardNavInputFocus,
  onForwardNavInputFocus,
  onIframeWindowFocus,
  onIframeWindowBlur,
  parseAndStyleEnvironment,
  setInputManualUseOnly,
  formatCard,
  getCardScheme,
  isCardValid,
  getCardBIN,
  queryCardMetadataIfRequired,
  setAriaInvalidAttribute,
  isCoBadgedCard,
} from "../shared";
import { hasValue } from "../../utils/validation";
import { getSchemeChoiceOptionSchemeName } from "../SchemeChoice/schemeChoiceOption";
import SchemeChoiceCardNumberAdapter from "./SchemeChoiceCardNumberAdapter";

class CardNumber {
  state = {
    scheme: "",
    value: "",
    validationStatus: VALIDATION_STATUSES.EMPTY,
    bin: "",
    cvvHidden: false,
    cardMetadata: {},
    chosenScheme: "",
  };

  previousState;
  input;
  schemeChoice;

  copyPasteDisabled = false;

  constructor(env) {
    this.input = document.getElementById("checkout-frames-card-number");

    const parsedEnv = parseAndStyleEnvironment(env);
    const {
      MODES,
      acceptedPaymentMethods,
      isSchemeChoiceEnabled,
      APP_URL,
    } = parsedEnv;
    this.appUrl = APP_URL;
    this.isSchemeChoiceEnabled = isSchemeChoiceEnabled;

    this.copyPasteDisabled = hasValue(MODES, FRAMES_MODES.DISABLE_COPY_PASTE);
    this.state.cvvHidden = hasValue(MODES, FRAMES_MODES.CVV_HIDDEN);

    if (this.isSchemeChoiceEnabled) {
      this.schemeChoice = new SchemeChoiceCardNumberAdapter(parsedEnv);
    }

    if (hasValue(MODES, FRAMES_MODES.RIGHT_TO_LEFT)) {
      this.input.parentNode.setAttribute("dir", "rtl");
    }

    if (acceptedPaymentMethods && acceptedPaymentMethods.length > 0) {
      this.acceptedPaymentMethods = acceptedPaymentMethods;
    }

    this.registerEventHandlers();
    this.previousState = { ...this.state };

    sendToControllerIframe(ELEMENTS.CARD_NUMBER, {
      event: EVENTS.FRAME_ACTIVATED,
    });
  }

  registerEventHandlers() {
    this.input.addEventListener("mouseover", this.onMouseOver);
    this.input.addEventListener("mouseout", this.onMouseOut);
    registerFocusHandler(this.input, this.onFocus);
    this.input.addEventListener(
      "input",
      wrapOnInputForIE11(wrapOnInputForCompositionText(this.onInputChange))
    );
    this.input.addEventListener("blur", this.onBlur);
    this.input.addEventListener("compositionend", this.onInputChange);

    if (this.copyPasteDisabled) {
      setInputManualUseOnly(this.input);
    }

    document
      .getElementById("cc-exp-hidden")
      .addEventListener("input", onHiddenInputChange);
    document
      .getElementById("cc-csc-hidden")
      .addEventListener("input", onHiddenInputChange);

    window.addEventListener(
      "message",
      onMessage("cardNumber", this.onReceiveMessage)
    );

    this.backwardNavInput = document.getElementById("backward-nav-input");
    if (this.backwardNavInput) {
      this.backwardNavInput.addEventListener(
        "focus",
        onBackwardNavInputFocus(ELEMENT_FRAMES.CARD_NUMBER)
      );
    }

    this.forwardNavInput = document.getElementById("forward-nav-input");
    if (this.forwardNavInput) {
      this.forwardNavInput.addEventListener(
        "focus",
        onForwardNavInputFocus(ELEMENT_FRAMES.CARD_NUMBER)
      );
    }

    window.addEventListener(
      "focus",
      onIframeWindowFocus(
        this.input,
        this.backwardNavInput,
        this.forwardNavInput
      )
    );
    window.addEventListener(
      "blur",
      onIframeWindowBlur(this.backwardNavInput, this.forwardNavInput)
    );
  }

  get validationStatus() {
    return this.state.validationStatus;
  }

  updateValidationStatus = () => {
    this.previousState.validationStatus = this.state.validationStatus;

    const scheme = this.state.scheme || null;
    this.state.validationStatus = getNewValidationStatus(
      this.input.value,
      isCardValid,
      this.state.value,
      scheme
    );
  };

  /**
   * Handler when the card number changes
   * Update the state (value, validity)
   */
  onInputChange = (event) => {
    const changeValue = event.target.value;
    const formattedValue = formatCard(changeValue);

    if (this.isSchemeChoiceEnabled) {
      queryCardMetadataIfRequired({
        changedValue: changeValue,
        appState: this.state,
        appUrl: this.appUrl,
        onValueChange: this.schemeChoice?.toggleElementVisibility,
      });
    }

    this.state.value = removeSpaces(formattedValue);
    this.input.value = formattedValue;

    this.state.bin = getCardBIN(this.state.value);
    this.state.scheme = getCardScheme(this.state.value);

    if (this.state.scheme !== this.previousState.scheme) {
      this.previousState.scheme = this.state.scheme;
      this.emitPaymentMethodChanged(this.state.scheme);

      if (!this.state.cvvHidden) {
        // send to the cvv iframe for maximum length check
        sendToElementIframe(ELEMENT_FRAMES.CVV, {
          message: EVENTS.PAYMENT_METHOD_CHANGED,
          scheme: this.state.scheme,
        });
      }
    }

    this.updateValidationStatus();

    onAutofillInput(event);

    sendToControllerIframe(ELEMENTS.CARD_NUMBER, {
      event: EVENTS.VALUE_CHANGED,
      value: this.state.value,
      validationStatus: this.state.validationStatus,
      previousValidationStatus: this.previousState.validationStatus,
    });

    if (this.state.bin && this.state.bin !== this.previousState.bin) {
      this.previousState.bin = this.state.bin;
      this.emitCardBinChanged(this.state.scheme);
    }

    fixIE11InputCursor(this.input);
  };

  emitPaymentMethodChanged = (scheme) => {
    sendViaPostMessage(EVENTS.PAYMENT_METHOD_CHANGED, {
      isValid: this.state.validationStatus === VALIDATION_STATUSES.VALID,
      paymentMethod: scheme,
      isPaymentMethodAccepted:
        !this.acceptedPaymentMethods ||
        this.acceptedPaymentMethods.indexOf(scheme) >= 0,
    });
  };

  emitCardBinChanged = (scheme) => {
    const isCoBadged = isCoBadgedCard(this.state.cardMetadata.schemes);

    sendViaPostMessage(EVENTS.CARD_BIN_CHANGED, {
      bin: this.state.bin,
      isCoBadged,
      scheme,
    });
  };

  onMouseOver = () => {
    setOnMouseOverClasses(this.input, "card-number");
  };

  onMouseOut = () => {
    setOnMouseOutClasses(this.input, "card-number");
  };

  /**
   * Handler when the input obtains focus
   */
  onFocus = () => {
    sendToControllerIframe(ELEMENTS.CARD_NUMBER, {
      event: EVENTS.FRAME_FOCUS,
      validationStatus: this.state.validationStatus,
    });

    setOnFocusClasses(this.input, "card-number");
  };

  /**
   * Handler when the input loses focus
   */
  onBlur = () => {
    sendToControllerIframe(ELEMENTS.CARD_NUMBER, {
      event: EVENTS.FRAME_BLUR,
      validationStatus: this.state.validationStatus,
    });
    setAriaInvalidAttribute(this.input, this.state.validationStatus);
    setOnBlurClasses(this.input, "card-number", this.state);
  };

  onReceiveMessage = (e) => {
    const { message, style, value, element } = e.data.data;
    switch (message) {
      case "initial-config":
        if (style)
          handleCustomInputStyle({
            style,
            element: ELEMENTS.CARD_NUMBER,
            isSchemeChoiceEnabled: this.isSchemeChoiceEnabled,
          });
        break;
      case "clear":
        this.state.value = "";
        break;
      case "forceFocus":
        this.input.focus();
        break;
      case "forceBlur":
        this.onBlur();
        break;
      case "autofilled":
        simulateAutofill(this.input, value);
        break;
      case EVENTS.VALUE_CHANGED:
        if (element === ELEMENTS.SCHEME_CHOICE) {
          this.state.chosenScheme = value;

          const currentScheme =
            this.state.chosenScheme || this.state.cardMetadata.schemes?.scheme;

          const schemeName =
            getSchemeChoiceOptionSchemeName(currentScheme) || this.state.scheme;
          this.emitPaymentMethodChanged(schemeName);
          this.emitCardBinChanged(schemeName);
        }
        break;
      default:
        break;
    }
  };
}

export default CardNumber;
